import React, { useState, useEffect, useCallback } from "react";
import {
    Grid,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Button,
    Divider,
    Card,
    CardActionArea,
    CardMedia,
    CardContent,
    Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import CloseIcon from "@material-ui/icons/Close";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import { useDispatch, useSelector } from "react-redux";
import { eventPhotosFunc } from "../../../../data-store/actions/events-actions";
import SimpleDialogDemo from "../../Modals/SimpleDialog";
import CreateRegistryJuezFromPhoto from "../../reports/atleteDetail/CreateRegistryJuezFromPhoto";

const useStyles = makeStyles((theme) => ({
    section: { marginBottom: theme.spacing(6) },
    thumbCard: {
        height: "100%",
    },
    thumbImg: {
        width: "100%",
        height: 200,
        objectFit: "cover",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
    },
    thumbTime: {
        fontSize: 12,
        color: "#777",
        textAlign: "center",
        marginTop: theme.spacing(1),
    },
    dialogHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 3),
        borderBottom: "1px solid #eee",
    },
    dialogStage: {
        position: "relative",
        background: "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 320,
        padding: theme.spacing(2),
    },
    dialogImg: { maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" },
    dialogInfo: { padding: theme.spacing(3) },
    dialogActions: { padding: theme.spacing(2, 3), textAlign: "center" },
    navButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        background: "#fff",
        color: "#ff4400",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        zIndex: 999,
        "&:hover": { background: "#f5f5f5" },
    },
    chipFolio: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontWeight: 600,
    },
    chipDorsal: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    puntoCard: {
        width: 120,
        textAlign: "center",
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
        borderRadius: 8,
    },
    puntoMedia: {
        height: 80,
        objectFit: "cover",
    },
    icono: {
        verticalAlign: "middle",
        marginRight: 4,
        fontSize: 18,
    },
}));

const ITEMS_PER_PAGE = 20;

const getTimeRange = (fotos, startIndex, endIndex) => {
    const inicio = new Date(fotos[startIndex]?.horaAjustada).toLocaleTimeString();
    const fin = new Date(fotos[endIndex - 1]?.horaAjustada).toLocaleTimeString();
    return `${inicio} - ${fin}`;
};

// foto.dorsales puede venir como arreglo o como objeto (llaves tipo Firebase
// push-id) según cómo lo haya escrito la app móvil — se normaliza a arreglo.
const obtenerDorsalesDetectados = (foto) => {
    if (!foto || !foto.dorsales) return [];
    return Array.isArray(foto.dorsales)
        ? foto.dorsales
        : Object.values(foto.dorsales);
};

const PhotoGalleryView = ({ eventoIndex }) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const eventPhotos = useSelector((state) => state.events.eventPhotos);

    const [open, setOpen] = useState(false);
    const [activePhotos, setActivePhotos] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [pages, setPages] = useState({});
    const [puntoActivo, setPuntoActivo] = useState(null);

    const handleOpen = (photos, index) => {
        setActivePhotos(photos);
        setCurrentIndex(index);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentIndex(0);
        setActivePhotos([]);
    };

    const handleKeyDown = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === "ArrowRight") handleNext();
            if (e.key === "ArrowLeft") handlePrev();
            if (e.key === "Escape") handleClose();
        },
        [open, currentIndex, activePhotos]
    );

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        dispatch(eventPhotosFunc(eventoIndex));
    }, [eventoIndex]);

    // El modal navega sobre TODAS las fotos del punto activo (no solo las
    // de la página actual) — así "Siguiente"/"Anterior" puede cruzar de una
    // página de tiempo a la otra sin quedarse atascado en el último/primer
    // elemento de la página. Esto mantiene sincronizado el botón de página
    // resaltado en la galería con la foto que se está viendo en el modal.
    useEffect(() => {
        if (!open || !puntoActivo) return;
        const newPage = Math.floor(currentIndex / ITEMS_PER_PAGE);
        setPages((prev) =>
            prev[puntoActivo] === newPage
                ? prev
                : { ...prev, [puntoActivo]: newPage }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, open]);

    useEffect(() => {
        if (eventPhotos?.fotos && !puntoActivo) {
            const primerPunto = Object.keys(eventPhotos.fotos)[0];
            setPuntoActivo(primerPunto);
        }
    }, [eventPhotos, puntoActivo]);

    const handleNext = () => {
        setCurrentIndex((prev) =>
            prev < activePhotos.length - 1 ? prev + 1 : prev
        );
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    };

    const handlePageChange = (punto, newPage) => {
        setPages((prev) => ({
            ...prev,
            [punto]: newPage,
        }));
    };

    const fotosDelPunto = eventPhotos?.fotos?.[puntoActivo] || {};
    const page = pages[puntoActivo] || 0;
    const allFotos = Object.values(fotosDelPunto);
    const paginatedFotos = allFotos.slice(
        page * ITEMS_PER_PAGE,
        (page + 1) * ITEMS_PER_PAGE
    );




    return (
        <div>
            {/* Selector de puntos fotográficos */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                {eventPhotos && eventPhotos.fotos &&
                    Object.entries(eventPhotos.fotos).map(([punto, fotos]) => {
                        const primerFoto = Object.values(fotos)[0];
                        const nombrePunto =
                            eventPhotos.puntosFotograficos?.[punto]?.descripcionLugar || `Punto ${punto}`;
                        return (
                            <Grid item key={punto}>
                                <Card
                                    className={classes.puntoCard}
                                    raised={puntoActivo === punto}
                                    onClick={() => setPuntoActivo(punto)}
                                >
                                    <CardActionArea>
                                        <CardMedia
                                            component="img"
                                            image={primerFoto?.urlThumb}
                                            alt={primerFoto?.filename}
                                            className={classes.puntoMedia}
                                        />
                                        <CardContent>
                                            <Typography variant="body2">
                                                <CameraAltIcon className={classes.icono} />
                                                {nombrePunto}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        );
                    })}
            </Grid>

            {/* Galería del punto activo */}
            {puntoActivo && paginatedFotos.length > 0 && (
                <div className={classes.section}>
                    <Typography variant="h6" gutterBottom>
                        {eventPhotos.puntosFotograficos?.[puntoActivo]?.descripcionLugar || `Punto ${puntoActivo}`}
                    </Typography>

                    <Grid container spacing={2}>
                        {paginatedFotos.map((foto, index) => (
                            <Grid item xs={6} sm={3} key={foto.filename}>
                                <Card className={classes.thumbCard}>
                                    <CardActionArea
                                        onClick={() =>
                                            handleOpen(allFotos, page * ITEMS_PER_PAGE + index)
                                        }
                                    >
                                        <CardMedia
                                            component="img"
                                            image={foto.urlThumb}
                                            alt={foto.filename}
                                            className={classes.thumbImg}
                                        />
                                        <CardContent>
                                            <Typography className={classes.thumbTime}>
                                                {new Date(foto.horaAjustada).toLocaleTimeString()}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Paginación por hora */}
                    <Grid
                        container
                        spacing={1}
                        justify="center"
                        alignItems="center"
                        style={{ marginTop: 16, flexWrap: "wrap" }}
                    >
                        {Array.from({
                            length: Math.ceil(allFotos.length / ITEMS_PER_PAGE),
                        }).map((_, i) => {
                            const start = i * ITEMS_PER_PAGE;
                            const end = Math.min(start + ITEMS_PER_PAGE, allFotos.length);
                            const label = getTimeRange(allFotos, start, end);
                            return (
                                <Grid item key={i}>
                                    <Button
                                        variant={page === i ? "contained" : "outlined"}
                                        color="primary"
                                        size="small"
                                        onClick={() => handlePageChange(puntoActivo, i)}
                                    >
                                        {label}
                                    </Button>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Divider style={{ marginTop: 24 }} />
                </div>
            )}

            {/* Modal visor de imagen */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogContent style={{ padding: 0 }}>
                    <div className={classes.dialogHeader}>
                        <Typography variant="h6">
                            {activePhotos[currentIndex]?.horaAjustada &&
                                new Date(activePhotos[currentIndex].horaAjustada).toLocaleString()}
                        </Typography>
                        <IconButton onClick={handleClose} aria-label="cerrar">
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className={classes.dialogStage}>
                        {currentIndex > 0 && (
                            <IconButton
                                onClick={handlePrev}
                                className={classes.navButton}
                                style={{ left: 10 }}
                            >
                                <ArrowBackIosIcon />
                            </IconButton>
                        )}
                        <img
                            src={activePhotos[currentIndex]?.url}
                            alt=""
                            className={classes.dialogImg}
                        />
                        {currentIndex < activePhotos.length - 1 && (
                            <IconButton
                                onClick={handleNext}
                                className={classes.navButton}
                                style={{ right: 10 }}
                            >
                                <ArrowForwardIosIcon />
                            </IconButton>
                        )}
                    </div>

                    <Divider />

                    <Grid container spacing={3} className={classes.dialogInfo}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline" color="textSecondary">
                                Folio
                            </Typography>
                            <div>
                                {activePhotos[currentIndex]?.folio ? (
                                    <Chip
                                        label={`Folio ${activePhotos[currentIndex].folio}`}
                                        className={classes.chipFolio}
                                    />
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        Sin folio registrado
                                    </Typography>
                                )}
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline" color="textSecondary">
                                Números detectados
                            </Typography>
                            <div>
                                {obtenerDorsalesDetectados(activePhotos[currentIndex]).length > 0 ? (
                                    obtenerDorsalesDetectados(activePhotos[currentIndex]).map(
                                        (numero, i) => (
                                            <Chip
                                                key={i}
                                                label={numero}
                                                className={classes.chipDorsal}
                                            />
                                        )
                                    )
                                ) : (
                                    <Typography variant="body2" color="textSecondary">
                                        Ninguno
                                    </Typography>
                                )}
                            </div>
                        </Grid>
                    </Grid>

                    <Divider />

                    <div className={classes.dialogActions}>
                        <SimpleDialogDemo buttonText={"Registrar un número de la foto"}>
                            <CreateRegistryJuezFromPhoto photo={activePhotos[currentIndex]} />
                        </SimpleDialogDemo>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PhotoGalleryView;
